import { WrapperEntitySelector } from '@components/Inputs';
import Misc from '@components/Misc';
import { Grid, TextInput, Typography, Accordion, DateTimeInput } from '@ts/storybook';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { Icon, Tooltip } from '@components/Outputs';
import { IForm } from '@ts/ui-models';

const LATITUDE = 'y';
const LONGITUDE = 'x';
const Z = 'z';
const NEW = 'new';

interface IFormEventProps extends IForm {}

const FormEvent = ({ values, requiredFields, onChange, onCallbackChange, mode }: IFormEventProps) => {
  const isViewMode = mode === 'view';
  const isNewMode = mode === 'new';
  const editDisabled = mode !== NEW;

  //#region Handlers

  const onChangeValue = attr => value => {
    onChange(attr, value);
  };

  const onChangeEntity = (entity, entityName, fkField) => {
    if (Array.isArray(entity)) {
      onChange(entityName, entity);
      onChange(fkField, entity);
    } else {
      const value = entity ? entity.getID() : null;
      //onChange( entityName, entity });
      onChange(fkField, value);
    }
  };

  const onChangeCoords = (type, coord) => ev => {
    let value = ev.target.value;
    const baseCoords = getCoordsFromProps(type);
    const coordinates = { ...baseCoords, [coord]: value };
    onChange(type, coordinates);
  };
  //#endregion

  //#region Funtions
  const getCoordsFromProps = type => {
    const coordinates = values[type] || {};
    const y = coordinates.get ? coordinates.get(LATITUDE) : coordinates.y;
    const x = coordinates.get ? coordinates.get(LONGITUDE) : coordinates.x;
    const z = coordinates.get ? coordinates.get(Z) : coordinates.z;
    return { x, y, z };
  };
  //#endregion

  //#region Render

  const _renderTimestamp = () => {
    return (
      <>
        <Typography variant="body1" className="font-bold" text="Date and time" />
        <Grid container size={{ xs: 12, md: 6, lg: 6 }}>
          <DateTimeInput
            isReadOnly={isViewMode || editDisabled}
            onChange={onChangeValue('timestamp')}
            value={values.timestamp}
            label={'Recorded date'}
          />
        </Grid>
      </>
    );
  };

  const _renderLocationCoordinates = () => {
    const { x, y } = getCoordsFromProps('GPSCoordinates');
    const { x: localX, y: localY, z: localZ } = getCoordsFromProps('localCoordinates');

    return (
      <Accordion title="Location coordinates" open={!isViewMode}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 3 }}>
            <TextInput isReadOnly label="Type" value={'GPS'} />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <TextInput
              isReadOnly={isViewMode || editDisabled}
              label="Latitude"
              value={y}
              type="number"
              onChange={onChangeCoords('GPSCoordinates', LATITUDE)}
            />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <TextInput
              isReadOnly={isViewMode || editDisabled}
              label="Longitude"
              value={x}
              type="number"
              onChange={onChangeCoords('GPSCoordinates', LONGITUDE)}
            />
          </Grid>
          <Grid size={{ xs: 3 }} />
          <Grid size={{ xs: 3 }}>
            <TextInput isReadOnly={true} label={'Type'} value={'Local'} />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <TextInput
              isReadOnly={isViewMode || editDisabled}
              label="X"
              value={localX}
              type="number"
              onChange={onChangeCoords('localCoordinates', LONGITUDE)}
            />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <TextInput
              isReadOnly={isViewMode || editDisabled}
              label="Y"
              value={localY}
              type="number"
              onChange={onChangeCoords('localCoordinates', LATITUDE)}
            />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <TextInput
              isReadOnly={isViewMode || editDisabled}
              label="Z"
              value={localZ}
              type="number"
              onChange={onChangeCoords('localCoordinates', Z)}
            />
          </Grid>
        </Grid>
      </Accordion>
    );
  };

  //#region Items
  const renderTitleAndInfoText = (title, infoHelp) => {
    return (
      <>
        <Grid container spacing={1}>
          <Grid>
            <Typography variant="h6" className="mb-2.5" text={title} />
          </Grid>
          {isViewMode && (
            <Grid>
              <Tooltip title={infoHelp}>
                <Icon size={23} icon={'help'} />
              </Tooltip>
            </Grid>
          )}
        </Grid>
        {!isViewMode && <Typography text={infoHelp} />}
      </>
    );
  };

  const renderRelatedItems = () => {
    const items = values.items;

    return (
      <>
        {renderTitleAndInfoText(
          'Related items',
          'A related item is an item involved in an event. Events can have multiple related items, allowing you to include all items affected or relevant to the event. You can select multiple related items to fully capture all necessary associations.'
        )}
        <WrapperEntitySelector
          id="items"
          value={items}
          onChange={item => onChangeEntity(item, 'items', 'itemId')}
          searchComponent={generateSearchEntitySelector('item')}
          isReadOnly={isViewMode || editDisabled}
          required={requiredFields.includes('item')}
          multiple
          seeAllSelected
          dataGridProps={{ showHeaderCheckbox: true }}
        />
      </>
    );
  };

  const renderTriggerItems = () => {
    const triggerItem = values.triggerItem ?? values.triggerItemId;

    return (
      <>
        {renderTitleAndInfoText(
          'Trigger item',
          'A trigger item is the specific item that caused the creation of an event. This item is the primary reason the event was generated, providing context and clarity about the event’s origin. Only one trigger item can be selected for each event.'
        )}
        <WrapperEntitySelector
          id="triggerItem"
          value={triggerItem}
          onChangeEntity={item => onChangeEntity(item, 'triggerItem', 'triggerItemId')}
          searchComponent={generateSearchEntitySelector('item')}
          isReadOnly={isViewMode || editDisabled}
        />
      </>
    );
  };

  const renderUsers = () => {
    const user = values.user;

    return (
      <>
        {renderTitleAndInfoText(
          'User',
          'A user, who may be associated with the event, is the individual responsible for initiating or creating the event. This is an optional field, allowing you to track accountability when necessary and provide insight into the events origin.'
        )}
        <WrapperEntitySelector
          id="user"
          searchComponent={generateSearchEntitySelector('user')}
          isReadOnly={isViewMode || editDisabled}
          value={user}
          onChangeEntity={user => onChangeEntity(user, 'user', 'userId')}
        />
      </>
    );
  };

  const renderLocation = () => {
    const location = values.location;
    return (
      <>
        {renderTitleAndInfoText(
          'Location',
          'The location refers to the specific place or area where the event occurred. It provides critical context for understanding the environment or setting in which the event took place.'
        )}
        <WrapperEntitySelector
          id="location"
          searchComponent={generateSearchEntitySelector('location')}
          value={location}
          isReadOnly={isViewMode || editDisabled}
          onChangeEntity={location => onChangeEntity(location, 'location', 'locationId')}
        />
      </>
    );
  };
  //#endregion

  const renderModule1 = () => {
    return (
      <Grid container spacing={2}>
        {!isNewMode && (
          <Grid size={{ xs: 12 }} sx={{ position: 'sticky', top: '-15px', backgroundColor: 'white', zIndex: 1, display: 'flex' }}>
            <Typography variant="h6" text={['ID: ', values.id]} />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <TextInput
            label="Name"
            isReadOnly={isViewMode}
            value={values.name}
            onChangeValue={onCallbackChange('name')}
            required={requiredFields.includes('name')}
            infoText="Blank spaces at the beggining and the end will be trimmed on save"
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput
            label="Event description"
            isReadOnly={isViewMode}
            value={values.description}
            onChange={onCallbackChange('description')}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <WrapperEntitySelector
            label={'Event type'}
            value={values.eventTypeId}
            onChangeEntity={entity => onChangeEntity(entity, 'eventType', 'eventTypeId')}
            searchComponent={generateSearchEntitySelector('eventType')}
            isReadOnly={isViewMode || editDisabled}
            required={requiredFields.includes('eventType')}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>{_renderTimestamp()}</Grid>
        {_renderLocationCoordinates()}
        <Accordion title="Extra information" open={!isViewMode}>
          <Grid size={{ xs: 12 }}>
            <TextInput
              label="Notes"
              isReadOnly={isViewMode}
              value={values.notes}
              onChange={onCallbackChange('notes')}
              multiline
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextInput
              label="Other details"
              disabled
              value={values.details ? JSON.stringify(values.details) : ''}
              //onChange={onCallbackChange('details')}
              multiline
            />
          </Grid>
        </Accordion>
      </Grid>
    );
  };
  const renderModule2 = () => {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>{renderRelatedItems()}</Grid>
        <Grid size={{ xs: 12 }}>{renderTriggerItems()}</Grid>
        <Grid size={{ xs: 12 }}>{renderUsers()}</Grid>
        <Grid size={{ xs: 12 }}>{renderLocation()}</Grid>
      </Grid>
    );
  };
  //#endregion

  return (
    <Grid container size={{ xs: 12 }} rowSpacing={2} columnSpacing={1} pb={2} id="general-tab">
      <Grid size={{ xs: 12, md: 12, lg: 6 }}>
        <Misc.NewPaperWithPadding>{renderModule1()}</Misc.NewPaperWithPadding>
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 6 }}>
        <Misc.NewPaperWithPadding>{renderModule2()}</Misc.NewPaperWithPadding>
      </Grid>
    </Grid>
  );
};

export default FormEvent;
